import { GraphQLClient } from 'graphql-request'
import { ensureValidToken, forceRefresh } from '@/core/auth/token-manager'
import { env } from '@/core/config/env'

const BASE_HEADERS = {
  'Content-Type': 'application/json',
  'x-app-platform': 'web',
}

/**
 * 401 detection — mirrors iOS GraphClient.isUnauthorizedError().
 */
function isUnauthorizedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false

  const resp = error as {
    response?: {
      status?: number
      errors?: Array<{
        message?: string
        extensions?: Record<string, unknown>
      }>
    }
  }

  if (resp.response?.status === 401) return true

  const errors = resp.response?.errors
  if (!errors) return false

  return errors.some((e) => {
    const ext = e.extensions
    if (ext) {
      const code = String(ext.code ?? '').toLowerCase()
      if (code === 'unauthenticated') return true

      const statusCode = Number(ext.statusCode)
      if (statusCode === 401) return true

      const original = ext.originalError as
        | { statusCode?: number; message?: string }
        | undefined
      if (original) {
        if (Number(original.statusCode) === 401) return true
        if (
          typeof original.message === 'string' &&
          original.message.toLowerCase().includes('unauthorized')
        )
          return true
      }
    }

    const msg = (e.message ?? '').toLowerCase()
    return (
      msg.includes('401') ||
      msg.includes('unauthorized') ||
      msg.includes('unauthenticated')
    )
  })
}

const client = new GraphQLClient(env.graphqlUrl, {
  headers: BASE_HEADERS,
  requestMiddleware: async (request) => {
    const token = await ensureValidToken()
    if (!token) {
      // No valid session — do NOT send a tokenless GraphQL request. Every
      // leaply-web GraphQL op (me, plan, …) requires auth, so a missing token
      // is logged by the backend as a "No auth token" 401 (Sentry BACKEND-65,
      // the iOS-webview flood). Throwing here aborts the request before it goes
      // out; ensureValidToken already cleared the session + triggered re-auth
      // on refresh failure. Mirrors rest-client's guard.
      throw new Error('No valid session')
    }
    return {
      ...request,
      headers: {
        ...request.headers,
        ...BASE_HEADERS,
        Authorization: `Bearer ${token}`,
      },
    }
  },
})

// Patch .request() to add 401 → refresh → retry.
// Mirrors iOS GraphClient.runWithRetry().
// Preserves original signature and types.
const originalRequest = client.request.bind(client)
client.request = (async (...args: Parameters<typeof originalRequest>) => {
  try {
    return await originalRequest(...args)
  } catch (error) {
    if (!isUnauthorizedError(error)) throw error

    const newToken = await forceRefresh()
    if (!newToken) throw error

    client.setHeader('Authorization', `Bearer ${newToken}`)
    return originalRequest(...args)
  }
}) as typeof originalRequest

export const graphqlClient = client
